import type { AdminSyncJobId } from '@sea-snag/shared';
import {
  Badge,
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@sea-snag/ui';
import { createFileRoute } from '@tanstack/react-router';
import { HTTPError } from 'ky';
import { ExternalLink, Loader2, Play } from 'lucide-react';
import { useState } from 'react';
import { toast } from 'sonner';

import { useAdminJobs, useRunAdminJob } from '../../hooks/use-admin-jobs';

export const Route = createFileRoute('/admin/jobs')({
  component: AdminJobsPage,
});

async function extractErrorMessage(err: unknown): Promise<string> {
  if (err instanceof HTTPError) {
    const body = (await err.response.json().catch(() => ({}))) as { message?: string };
    if (body.message) return body.message;
  }
  if (err instanceof Error && err.message) return err.message;
  return 'Failed to queue job';
}

function AdminJobsPage() {
  const { data: jobs } = useAdminJobs();
  const runJob = useRunAdminJob();
  const [pendingId, setPendingId] = useState<AdminSyncJobId | null>(null);

  function handleRun(id: AdminSyncJobId) {
    setPendingId(id);
    runJob.mutate(id, {
      onSuccess: (res) => {
        toast.success(res.message);
      },
      onError: async (err) => {
        const message = await extractErrorMessage(err);
        toast.error(message);
      },
      onSettled: () => {
        setPendingId(null);
      },
    });
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold tracking-tight">Sync Jobs</h1>
        <p className="text-muted-foreground">
          Manually trigger MMK agency-level sync jobs. Jobs are enqueued in BullMQ — inspect running
          state in{' '}
          <a
            href="/admin/queues"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1 underline hover:text-foreground"
          >
            Bull Board <ExternalLink className="size-3" />
          </a>
          .
        </p>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Agency Sync Jobs ({jobs.length})</CardTitle>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Name</TableHead>
                <TableHead>Description</TableHead>
                <TableHead>Queue</TableHead>
                <TableHead className="text-right">Action</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {jobs.map((job) => {
                const isPending = pendingId === job.id;
                return (
                  <TableRow key={job.id}>
                    <TableCell className="font-medium">{job.name}</TableCell>
                    <TableCell className="text-muted-foreground">{job.description}</TableCell>
                    <TableCell>
                      <Badge variant="secondary" className="font-mono text-xs">
                        {job.queue}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-right">
                      <Button
                        size="sm"
                        variant="outline"
                        disabled={pendingId !== null}
                        onClick={() => handleRun(job.id)}
                      >
                        {isPending ? (
                          <Loader2 className="mr-1 size-4 animate-spin" />
                        ) : (
                          <Play className="mr-1 size-4" />
                        )}
                        Run
                      </Button>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
}
